import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { play } from "../lib/sound";

const HOME_BY_ROLE = { customer: "/esplora", business: "/business", rider: "/rider", ops: "/ops", ceo: "/ceo", admin: "/ceo", professionista: "/pro" };
const ZONES = ["San Maurizio Canavese", "Ciriè", "San Carlo Canavese", "Caselle Torinese", "Borgaro Torinese", "Venaria Reale", "Settimo Torinese", "Torino"];

const BIZ_EMPTY = {
  brand_name: "", legal_name: "", contact_name: "", email: "", password: "",
  phone: "", address: "", zone: "", cap: "", vat_or_tax: "", category: "",
  opening_hours: "", description: "", image: "",
};

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // register - ruolo + campi base (customer/rider)
  const [rRole, setRRole] = useState("customer");
  const [rUsername, setRUsername] = useState(""); const [rFirstName, setRFirstName] = useState(""); const [rLastName, setRLastName] = useState(""); const [rEmail, setREmail] = useState("");
  const [rPw, setRPw] = useState(""); const [rPhone, setRPhone] = useState(""); const [rAddr, setRAddr] = useState("");
  // register - attività
  const [biz, setBiz] = useState(BIZ_EMPTY);
  const [cTerms, setCTerms] = useState(false);
  const [cPrivacy, setCPrivacy] = useState(false);
  const [cContract, setCContract] = useState(false);
  // recupero password
  const [recEmail, setRecEmail] = useState("");
  const [recSent, setRecSent] = useState(false);

  const setB = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });
  const go = (u) => navigate(from || HOME_BY_ROLE[u.role] || "/esplora");

  const submitLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await login(email, password); play("login"); toast.success(`Benvenuto, ${u.name}`); go(u); }
    catch (err) { console.error("login error", err); toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const bizRequiredFilled = ["brand_name", "legal_name", "contact_name", "email", "password", "phone", "address", "zone", "cap", "vat_or_tax", "category", "opening_hours", "description"].every((k) => (biz[k] || "").trim());
  const allConsents = cTerms && cPrivacy && cContract;
  const bizCanSubmit = bizRequiredFilled && allConsents;

  const submitRegister = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let u;
      if (rRole === "business") {
        u = await register({
          role: "business", ...biz, email: biz.email.trim(),
          accept_terms: cTerms, accept_privacy: cPrivacy, accept_contract: cContract,
        });
        toast.success("Candidatura inviata per la verifica");
      } else {
        const refCode = (new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("fuma_ref") || "").toUpperCase();
        u = await register({ username: rUsername, first_name: rFirstName, last_name: rLastName, email: rEmail, password: rPw, phone: rPhone, address: rAddr, role: rRole, referral_code: refCode || undefined });
        toast.success("Registrazione completata");
      }
      play("register");
      go(u);
    } catch (err) { console.error("register error", err); toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };


  const requestReset = async () => {
    if (!recEmail) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: recEmail, origin: window.location.origin });
      setRecSent(true);
      toast.success("Se l'email è registrata, riceverai il link di reset.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const Consent = ({ id, checked, onChange, label, docSlug, docLabel }) => (
    <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
      <Checkbox data-testid={id} checked={checked} onCheckedChange={onChange} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#00E676] data-[state=checked]:border-[#00E676]" />
      <span>{label}{" "}
        <a href={`/legal/${docSlug}`} target="_blank" rel="noreferrer" data-testid={`link-${docSlug}`} className="text-[#00E676] hover:underline">{docLabel}</a>
      </span>
    </label>
  );

  return (
    <div className="min-h-screen grain flex flex-col lg:flex-row">
      <div className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{ backgroundImage: "linear-gradient(to top, #0A0A0C 5%, rgba(10,10,12,0.3)), url(https://images.pexels.com/photos/4393252/pexels-photo-4393252.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="relative z-10">
          <div className="text-[#00E676] font-display font-black text-5xl tracking-tighter">Fuma c'anduma</div>
          <p className="text-white/70 italic mt-2 text-lg" data-testid="login-slogan">"L'amichevole rider di quartiere"</p>
          <p className="text-zinc-300 mt-4 text-lg max-w-md leading-relaxed">Il delivery locale del Canavese e di Torino. Ristoranti, rider e attività, in un'unica piattaforma.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-6">
            <div className="text-[#00E676] font-display font-black text-3xl">Fuma c'anduma</div>
            <div className="text-white/60 italic text-sm mt-1">"L'amichevole rider di quartiere"</div>
          </div>
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-3 w-full bg-[#1C1C1F]">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
              <TabsTrigger value="recupero" data-testid="tab-recupero">Recupero</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={submitLogin} className="space-y-4 mt-6">
                <div><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="email@esempio.it" /></div>
                <div><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="••••••" /></div>
                <Button data-testid="login-submit" disabled={loading} className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full">{loading ? "Attendere…" : "Accedi"}</Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={submitRegister} className="space-y-3 mt-6 max-h-[70vh] overflow-y-auto pr-1">
                <div><Label>Tipo di account</Label>
                  <Select value={rRole} onValueChange={setRRole}>
                    <SelectTrigger data-testid="reg-role" className="bg-[#1C1C1F] border-white/10 mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="customer">Cliente</SelectItem><SelectItem value="business">Attività commerciale</SelectItem><SelectItem value="rider">Rider</SelectItem></SelectContent>
                  </Select>
                </div>

                {rRole !== "business" ? (
                  <>
                    <div><Label>Username <span className="text-zinc-500 font-normal text-xs">(per le classifiche)</span></Label><Input data-testid="reg-username" value={rUsername} onChange={(e) => setRUsername(e.target.value)} className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="es. mario_rossi" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Nome</Label><Input data-testid="reg-first-name" value={rFirstName} onChange={(e) => setRFirstName(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                      <div><Label>Cognome</Label><Input data-testid="reg-last-name" value={rLastName} onChange={(e) => setRLastName(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    </div>
                    <div><Label>Email</Label><Input data-testid="reg-email" type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div><Label>Password</Label><Input data-testid="reg-password" type="password" value={rPw} onChange={(e) => setRPw(e.target.value)} required className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Telefono</Label><Input data-testid="reg-phone" value={rPhone} onChange={(e) => setRPhone(e.target.value)} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                      <div><Label>Indirizzo</Label><Input data-testid="reg-address" value={rAddr} onChange={(e) => setRAddr(e.target.value)} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    </div>
                    <Button type="submit" data-testid="reg-submit" disabled={loading} className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full">{loading ? "Attendere…" : "Crea account"}</Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-zinc-500">Compila i dati della candidatura. Dopo l'invio la tua attività resta <b>in attesa di verifica</b> e non è visibile nel marketplace finché non viene approvata.</p>
                    <div><Label>Nome attività</Label><Input data-testid="biz-brand" value={biz.brand_name} onChange={setB("brand_name")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div><Label>Ragione sociale</Label><Input data-testid="biz-legal" value={biz.legal_name} onChange={setB("legal_name")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div><Label>Referente (nome e cognome)</Label><Input data-testid="biz-contact" value={biz.contact_name} onChange={setB("contact_name")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Email</Label><Input data-testid="biz-email" type="email" value={biz.email} onChange={setB("email")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                      <div><Label>Password</Label><Input data-testid="biz-password" type="password" value={biz.password} onChange={setB("password")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Telefono</Label><Input data-testid="biz-phone" value={biz.phone} onChange={setB("phone")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                      <div><Label>Comune</Label>
                        <Select value={biz.zone} onValueChange={(v) => setBiz({ ...biz, zone: v })}>
                          <SelectTrigger data-testid="biz-zone" className="bg-[#1C1C1F] border-white/10 mt-1.5"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                          <SelectContent>{ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Indirizzo completo</Label><Input data-testid="biz-address" value={biz.address} onChange={setB("address")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                      <div><Label>CAP</Label><Input data-testid="biz-cap" value={biz.cap} onChange={setB("cap")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    </div>
                    <div><Label>Partita IVA (o Codice Fiscale)</Label><Input data-testid="biz-vat" value={biz.vat_or_tax} onChange={setB("vat_or_tax")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Categoria</Label><Input data-testid="biz-category" value={biz.category} onChange={setB("category")} className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="es. Pizzeria" /></div>
                      <div><Label>Orari di apertura</Label><Input data-testid="biz-hours" value={biz.opening_hours} onChange={setB("opening_hours")} className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="es. 12-15 / 19-23" /></div>
                    </div>
                    <div><Label>Descrizione dell'attività</Label><Textarea data-testid="biz-description" value={biz.description} onChange={setB("description")} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                    <div><Label>Logo (facoltativo in candidatura, obbligatorio prima della pubblicazione)</Label><div className="mt-1.5"><ImageUpload testid="biz-logo" folder="businesses" value={biz.image} onChange={(url) => setBiz({ ...biz, image: url })} /></div></div>

                    <div className="space-y-2 border-t border-white/10 pt-3">
                      <Consent id="consent-terms" checked={cTerms} onChange={setCTerms} label="Accetto i" docSlug="termini" docLabel="Termini e Condizioni" />
                      <Consent id="consent-privacy" checked={cPrivacy} onChange={setCPrivacy} label="Ho letto e accetto l'" docSlug="privacy" docLabel="Informativa Privacy (GDPR)" />
                      <Consent id="consent-contract" checked={cContract} onChange={setCContract} label="Accetto il" docSlug="contratto" docLabel="Contratto di collaborazione" />
                    </div>
                    <Button type="submit" data-testid="biz-submit" disabled={loading || !bizCanSubmit} className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full disabled:opacity-40">{loading ? "Invio…" : "Crea account e invia candidatura"}</Button>
                    {!allConsents && <p className="text-xs text-zinc-500 text-center">Accetta tutte le condizioni per proseguire.</p>}
                  </>
                )}
              </form>
            </TabsContent>

            <TabsContent value="recupero">
              {!recSent ? (
                <div className="space-y-4 mt-6">
                  <p className="text-sm text-zinc-400">Inserisci la tua email: ti invieremo un link per reimpostare la password.</p>
                  <div><Label>Email</Label><Input data-testid="rec-email" type="email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)} placeholder="email@esempio.it" className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
                  <Button data-testid="rec-request" onClick={requestReset} disabled={loading} className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full">{loading ? "Invio…" : "Invia link di recupero"}</Button>
                </div>
              ) : (
                <div data-testid="rec-sent" className="mt-6 flex flex-col items-center text-center gap-3 py-6">
                  <div className="h-14 w-14 rounded-full bg-[#00E676]/10 flex items-center justify-center"><MailCheck className="h-7 w-7 text-[#00E676]" /></div>
                  <p className="text-sm text-zinc-300">Se l'indirizzo è registrato, riceverai a breve un'email con il link per reimpostare la password (valido 1 ora).</p>
                  <button className="text-xs text-[#00E676] hover:underline" onClick={() => { setRecSent(false); setRecEmail(""); }}>Invia di nuovo</button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
